import { apiService } from "@/services/api";

export default {
  namespaced: true,

  state: {
    prices: [],
    history: {}, // { [section]: [ ...records ] }
    dolar: null,
    loading: false,
    error: null,
  },

  getters: {
    prices(state) {
      return state.prices;
    },
    dolar(state) {
      return state.dolar;
    },
    loading(state) {
      return state.loading;
    },
    getPricesBySection: (state) => (section) => {
      return state.prices.filter((p) => p.section === section);
    },
    getPriceByMetric: (state) => (section, metric) => {
      const cleanTarget = (metric || "").replace(/^precio_/, "").toLowerCase();
      return state.prices.find((p) => {
        if (p.section !== section) return false;
        if (p.metric === metric || p.metric_code === metric) return true;
        const cleanP = (p.metric || "").replace(/^precio_/, "").toLowerCase();
        const cleanCode = (p.metric_code || "")
          .replace(/^precio_/, "")
          .toLowerCase();
        return cleanP === cleanTarget || cleanCode === cleanTarget;
      });
    },
    getHistory: (state) => (section) => {
      return state.history[section] || [];
    },
  },

  mutations: {
    setPrices(state, data) {
      state.prices = data;
    },
    mergePrices(state, { section, data }) {
      // Reemplaza solo los precios de esa sección, preserva el resto
      const others = state.prices.filter((p) => p.section !== section);
      state.prices = [...others, ...data];
    },
    setDolar(state, data) {
      state.dolar = data;
    },
    setHistory(state, { section, data }) {
      state.history = { ...state.history, [section]: data };
    },
    setLoading(state, val) {
      state.loading = val;
    },
    setError(state, val) {
      state.error = val;
    },
    clearState(state) {
      state.prices = [];
      state.dolar = null;
      state.loading = false;
      state.error = null;
    },
  },

  actions: {
    async fetchLatest({ commit }, filters = {}) {
      if (!navigator.onLine) return [];
      commit("setLoading", true);
      commit("setError", null);
      try {
        const params = new URLSearchParams();
        if (filters.section) params.append("section", filters.section);
        if (filters.metric) params.append("metric", filters.metric);
        if (filters.region) params.append("region", filters.region);
        params.append("_t", Date.now()); // Cache buster
        const qs = params.toString();
        const url = `market-references/latest?${qs}`;
        const resp = await apiService.getCustom(url);
        const data = resp.data || [];
        if (filters.section) {
          // Merge: solo actualiza la sección solicitada, preserva las demás
          commit("mergePrices", { section: filters.section, data });
        } else {
          // Sin filtro: reemplaza todo
          commit("setPrices", data);
        }
        return data;
      } catch (e) {
        commit("setError", "Error cargando precios de referencia");
        console.warn("fetchLatest market refs:", e);
        return [];
      } finally {
        commit("setLoading", false);
      }
    },

    async fetchDolar({ commit }) {
      if (!navigator.onLine) return null;
      try {
        const resp = await apiService.getCustom("market-references/dolar");
        commit("setDolar", resp.data);
        return resp.data;
      } catch (e) {
        console.warn("fetchDolar:", e);
        return null;
      }
    },

    async compare(
      _,
      {
        section,
        metric,
        user_value,
        user_unit,
        period_year,
        period_month,
        region,
      }
    ) {
      if (!navigator.onLine) return null;
      try {
        const params = new URLSearchParams({
          section,
          metric,
          user_value: String(user_value),
          user_unit,
          period_year: String(period_year),
          period_month: String(period_month),
        });
        if (region) params.append("region", region);
        const resp = await apiService.getCustom(
          `market-references/compare?${params.toString()}`
        );
        return resp.data;
      } catch (e) {
        console.warn("compare market ref:", e);
        return null;
      }
    },

    async fetchHistory(
      { commit },
      { section, metric, region, year_from, year_to } = {}
    ) {
      if (!navigator.onLine) return [];
      try {
        const params = new URLSearchParams({ section });
        if (metric) params.append("metric", metric);
        if (region) params.append("region", region);
        if (year_from) params.append("year_from", String(year_from));
        if (year_to) params.append("year_to", String(year_to));
        const resp = await apiService.getCustom(
          `market-references/history?${params.toString()}`
        );
        const data = resp.data || [];
        commit("setHistory", { section, data });
        return data;
      } catch (e) {
        console.warn("fetchHistory market refs:", e);
        return [];
      }
    },
  },
};
